import React, { FC, useCallback, useContext } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { GetInventoryDocument, CreateSelfTrackedLearningDocument } from '@/store/v2';
import { UserSelfTrackedLearning } from '@/component/UserSelfTrackedLearning/types';
import { UserSelfTrackedLearningForm } from '@/component/UserSelfTrackedLearning/components/UserSelfTrackedLearningForm/UserSelfTrackedLearningForm';
import { SharedSnackbarContext, SnackbarType } from '@/component/SharedSnackbar/SharedSnackbar';
import { trackAddUserSelfTrackedLearning } from '@/utils/tracking/homePage';
import { getEffortForUser } from '@/store/analytics/learning-progress';
import { useCurrentUser } from '@/store/currentUser';

type AddUserSelfTrackedLearningProps = {
  onClose?: () => void;
};

export const AddUserSelfTrackedLearning: FC<AddUserSelfTrackedLearningProps> = ({ onClose }) => {
  const { t } = useTranslation('userSelfTrackedLearning');
  const { openSnackbar } = useContext(SharedSnackbarContext);
  const { user } = useCurrentUser();

  const [addUserSelfTrackedLearning, { loading }] = useMutation<{
    createSelfTrackedLearning: { success: boolean };
  }>(CreateSelfTrackedLearningDocument, {
    refetchQueries: [GetInventoryDocument, getEffortForUser],
  });

  const handleAddUserSelfTrackedLearning = useCallback(async (userSelfTrackedLearning: UserSelfTrackedLearning) => {
    trackAddUserSelfTrackedLearning(Math.floor(userSelfTrackedLearning.effort / 60));
    const { data } = await addUserSelfTrackedLearning({
      variables: { userSelfTrackedLearning: { ...userSelfTrackedLearning, userId: user?.id } },
    });

    if (data?.createSelfTrackedLearning?.success) {
      openSnackbar({
        type: SnackbarType.SUCCESS,
        message: t('Learning has been added'),
        isDismissive: true,
      });
    }

    if (onClose) {
      onClose();
    }
  }, []);

  return <UserSelfTrackedLearningForm loading={loading} save={handleAddUserSelfTrackedLearning} />;
};
